/**
 * Consent management for RetrofitIQ.
 *
 * GDPR/PECR-compliant approach:
 *  - Google Consent Mode v2 is set to "denied" by default in public/index.html
 *    BEFORE any tag loads.
 *  - No analytics/marketing scripts (GA4, PostHog) are loaded until the visitor
 *    explicitly grants analytics consent.
 *  - Necessary/essential cookies (security, functionality) are always allowed.
 *
 * Categories:
 *   necessary  — always on (cannot be disabled)
 *   analytics  — Google Analytics 4 + PostHog product analytics
 *   marketing  — reserved for future advertising/remarketing tags
 */

const GA_ID = "G-S6RKTLG6KZ";
const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";
const STORAGE_KEY = "riq:consent:v1";

export const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

let gaLoaded = false;
let phLoaded = false;

export function getStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function storeConsent(prefs) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...DEFAULT_PREFS, ...prefs, necessary: true, ts: Date.now() })
    );
  } catch {
    /* storage unavailable — non-fatal */
  }
}

function loadGA() {
  if (gaLoaded || typeof window === "undefined") return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  if (typeof window.gtag === "function") {
    window.gtag("config", GA_ID, { anonymize_ip: true, send_page_view: true });
  }
}

function loadPostHog() {
  if (phLoaded || typeof window === "undefined") return;
  phLoaded = true;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.crossOrigin = "anonymous"),
          (p.async = !0),
          (p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
            },
            u.people.toString = function () {
              return u.toString(1) + ".people (stub)";
            },
            o =
              "init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(
                " "
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
  });
  /* eslint-enable */
}

/**
 * Applies a consent decision: updates Google Consent Mode signals and
 * loads/initialises analytics tools only where consent is granted.
 */
export function applyConsent(prefs) {
  const analytics = !!prefs.analytics;
  const marketing = !!prefs.marketing;

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
    });
  }

  if (analytics) {
    loadGA();
    loadPostHog();
  }
}

/** Re-applies any previously stored consent on app load (returning visitors). */
export function initConsentOnLoad() {
  const stored = getStoredConsent();
  if (stored) applyConsent(stored);
}
